import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB");
};

const isSameDay = (value) => {
  if (!value) return false;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;

  const today = new Date();
  return (
    parsed.getFullYear() === today.getFullYear() &&
    parsed.getMonth() === today.getMonth() &&
    parsed.getDate() === today.getDate()
  );
};

const normalizeApprovalStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested" || normalized === "rejected" || normalized === "not approved") {
    return "Changes Requested";
  }
  return "Approval Pending";
};

const isCompletedStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "completed" || normalized === "closed";
};

const isNewerReport = (candidate, current) =>
  new Date(candidate?.updatedAt || candidate?.createdAt || 0) >
  new Date(current?.updatedAt || current?.createdAt || 0);

export default function AccountsComplaints() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusFilter = String(searchParams.get("status") || "").toLowerCase();
  const todayOnly = searchParams.get("today") === "1";
  const complaintFilter = String(searchParams.get("complaintId") || "").trim();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [complaintsRes, customersRes, visitsRes] = await Promise.all([
          axios.get(API_BASE_URL + "/api/complaints"),
          axios.get(API_BASE_URL + "/api/customers"),
          axios.get(API_BASE_URL + "/api/visits", {
            params: { excludePdf: 1 }
          })
        ]);

        const complaints = Array.isArray(complaintsRes.data) ? complaintsRes.data : [];
        const customers = Array.isArray(customersRes.data) ? customersRes.data : [];
        const visits = Array.isArray(visitsRes.data) ? visitsRes.data : [];

        const customerMap = new Map(customers.map((customer) => [String(customer._id), customer]));
        const reportMap = visits.reduce((acc, report) => {
          [report.complaintId, report.complaintRefId]
            .filter(Boolean)
            .forEach((key) => {
              const normalizedKey = String(key);
              if (!acc[normalizedKey] || isNewerReport(report, acc[normalizedKey])) {
                acc[normalizedKey] = report;
              }
            });
          return acc;
        }, {});

        const mappedRows = complaints.map((complaint) => {
          const report =
            reportMap[String(complaint._id)] ||
            reportMap[String(complaint.complaintId)] ||
            null;
          const customer = customerMap.get(String(complaint.customerId));
          const complaintStatus = complaint.status || "pending";
          const approvalStatus = normalizeApprovalStatus(report?.approvalStatus);
          const reportStatus = !report
            ? "Yet to be submitted"
            : approvalStatus === "Approved"
            ? (report.finalStatus || "Pending")
            : "Waiting for admin approval";

          return {
            id: complaint._id,
            complaintId: complaint.complaintId || "-",
            customer: customer?.name || complaint.company || "-",
            company: complaint.company || customer?.name || "-",
            engineer: complaint.engineerAssigned || report?.engineer || "-",
            complaintStatus,
            reportStatus,
            approvalStatus,
            createdAt: complaint.createdAt,
            visitDate: report?.visitDate || report?.date || complaint.visitDate || "",
            report
          };
        });

        setRows(mappedRows);
      } catch (error) {
        console.error("Failed to load accounts complaints", error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    setSearch(complaintFilter || "");
  }, [complaintFilter]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const normalizedComplaintStatus = String(row.complaintStatus || "").toLowerCase();
      const matchesComplaintFilter = complaintFilter
        ? String(row.id) === complaintFilter || String(row.complaintId) === complaintFilter
        : true;
      const matchesStatus =
        !statusFilter ||
        (statusFilter === "completed"
          ? isCompletedStatus(normalizedComplaintStatus)
          : normalizedComplaintStatus === statusFilter);
      const matchesToday = !todayOnly || isSameDay(row.createdAt);
      const matchesSearch = [
        row.complaintId,
        row.customer,
        row.company,
        row.engineer,
        row.complaintStatus,
        row.reportStatus
      ]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase());

      return matchesComplaintFilter && matchesStatus && matchesToday && matchesSearch;
    });
  }, [complaintFilter, rows, search, statusFilter, todayOnly]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Complaints & Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            Accounts can track complaint progress here and open report details only after admin approval.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/accounts/create-complaint")}
          className="inline-flex items-center justify-center rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-stone-950 shadow-md hover:bg-amber-400 transition whitespace-nowrap"
        >
          + Create Complaint
        </button>
      </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <input
            type="text"
            placeholder="Search complaints or reports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white p-3 md:max-w-md"
          />

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className={`rounded-full border px-4 py-2 text-sm ${!statusFilter && !todayOnly ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-white text-gray-700"}`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => setSearchParams({ status: "pending" })}
              className={`rounded-full border px-4 py-2 text-sm ${statusFilter === "pending" ? "border-amber-500 bg-amber-500 text-white" : "border-gray-300 bg-white text-gray-700"}`}
            >
              Pending
            </button>
            <button
              type="button"
              onClick={() => setSearchParams({ status: "completed" })}
              className={`rounded-full border px-4 py-2 text-sm ${statusFilter === "completed" ? "border-emerald-600 bg-emerald-600 text-white" : "border-gray-300 bg-white text-gray-700"}`}
            >
              Completed
            </button>
            <button
              type="button"
              onClick={() => setSearchParams({ today: "1" })}
              className={`rounded-full border px-4 py-2 text-sm ${todayOnly ? "border-violet-600 bg-violet-600 text-white" : "border-gray-300 bg-white text-gray-700"}`}
            >
              Today
            </button>
          </div>
        </div>

      <div className="overflow-hidden rounded-2xl bg-white shadow">
        <div className="space-y-4 p-4 lg:hidden">
          {loading && (
            <div className="rounded-2xl bg-stone-50 px-4 py-10 text-center text-stone-500">
              Loading complaints...
            </div>
          )}

          {!loading && filteredRows.length === 0 && (
            <div className="rounded-2xl bg-stone-50 px-4 py-10 text-center text-stone-500">
              No complaints found
            </div>
          )}

          {!loading && filteredRows.map((row) => {
            const reportApproved = row.approvalStatus === "Approved";

            return (
              <article key={row.id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-base font-bold text-gray-900">{row.complaintId}</p>
                    <p className="mt-1 text-sm text-gray-600">{row.customer}</p>
                  </div>
                  <span className="inline-flex rounded-full bg-slate-100 px-3 py-2 text-sm font-medium capitalize text-slate-700">
                    {row.complaintStatus}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <CompactField label="Company" value={row.company} />
                  <CompactField label="Engineer" value={row.engineer} />
                  <CompactField label="Visit Date" value={formatDate(row.visitDate)} />
                  <CompactField label="Report Status" value={row.reportStatus} />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span
                    className={`inline-flex rounded-full px-3 py-2 text-sm font-medium ${
                      reportApproved
                        ? "bg-emerald-100 text-emerald-700"
                        : row.approvalStatus === "Changes Requested"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {row.report ? row.approvalStatus : "No Report"}
                  </span>
                </div>

                <div className="mt-4">
                  {!row.report ? (
                    <span className="inline-flex rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-500">
                      Waiting for report
                    </span>
                  ) : reportApproved ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/accounts/reports/${row.report._id}`)}
                      className="rounded-lg bg-blue-600 px-3 py-2 text-white hover:bg-blue-700"
                    >
                      View Report
                    </button>
                  ) : (
                    <span className="inline-flex rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                      Wait for admin approval
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left">Complaint ID</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Company</th>
                <th className="p-3 text-left">Engineer</th>
                <th className="p-3 text-left">Complaint Status</th>
                <th className="p-3 text-left">Visit Date</th>
                <th className="p-3 text-left">Report Status</th>
                <th className="p-3 text-left">Admin Approval</th>
                <th className="p-3 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="p-4 text-center text-gray-500">
                    Loading complaints...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-4 text-center text-gray-500">
                    No complaints found
                  </td>
                </tr>
              )}

              {!loading && filteredRows.map((row) => {
                const reportApproved = row.approvalStatus === "Approved";

                return (
                  <tr key={row.id} className="border-t align-top">
                    <td className="p-3 font-medium">{row.complaintId}</td>
                    <td className="p-3">{row.customer}</td>
                    <td className="p-3">{row.company}</td>
                    <td className="p-3">{row.engineer}</td>
                    <td className="p-3">
                      <span className="inline-flex rounded-full bg-slate-100 px-3 py-2 text-sm font-medium capitalize text-slate-700">
                        {row.complaintStatus}
                      </span>
                    </td>
                    <td className="p-3">{formatDate(row.visitDate)}</td>
                    <td className="p-3">
                      <span
                        className={`inline-flex rounded-full px-3 py-2 text-sm font-medium ${
                          !row.report
                            ? "bg-yellow-100 text-yellow-700"
                            : reportApproved
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {row.reportStatus}
                      </span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-flex rounded-full px-3 py-2 text-sm font-medium ${
                          reportApproved
                            ? "bg-emerald-100 text-emerald-700"
                            : row.approvalStatus === "Changes Requested"
                            ? "bg-rose-100 text-rose-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {row.report ? row.approvalStatus : "No Report"}
                      </span>
                    </td>
                    <td className="p-3">
                      {!row.report ? (
                        <span className="inline-flex rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-500">
                          Waiting for report
                        </span>
                      ) : reportApproved ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/accounts/reports/${row.report._id}`)}
                          className="rounded-lg bg-blue-600 px-3 py-2 text-white hover:bg-blue-700"
                        >
                          View Report
                        </button>
                      ) : (
                        <span className="inline-flex rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                          Wait for admin approval
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CompactField({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</p>
      <p className="mt-2 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}


